import Job, {IDataSetJob, IJob} from './JobDto';

export interface IJobs {
  length: number;

  getJobsIds(): string[];
  getOnGoingJobs(): IJobs;
  getFinishedJobs(): IJobs;
  getRawData(): IDataSetJob[];
  getUniquesBinariesIds(): string[];
  getJobInstancesArray: () => IJob[];
  removeJobById(jobId: string): void;
  getJobsByBinaryId(binaryId: string): IJobs;
  getJobById(jobId: string): IJob | undefined;
  getJobsByDataSetId(dataSetId: string): IJobs;
  getJobsByGroupUploadedBatchId(groupUploadedBatchId: string): IJobs;
}

export default class Jobs implements IJobs {
  private _jobs: IJob[] = [];
  private _data: IDataSetJob[];

  get length() {
    return this._jobs.length;
  }

  constructor(jobsData: IDataSetJob[]) {
    this._data = jobsData;
    this._jobs = jobsData.map(job => new Job(job));
  }

  private getJobsInstance(jobs: IJob[]): IJobs {
    return new Jobs(jobs.map(job => job.getRawData()));
  }

  public getRawData() {
    return [...this._data];
  }

  public getJobInstancesArray() {
    return [...this._jobs];
  }

  public getJobsIds() {
    return this._jobs.map(job => job.getId());
  }

  public getUniquesBinariesIds() {
    let binariesIds: string[] = [];

    this._jobs.forEach(job => {
      const binaryId = job.getAssociatedBinaryId();

      if (binaryId && !binariesIds.includes(binaryId)) {
        binariesIds.push(binaryId);
      }
    });

    return binariesIds;
  }

  public getJobById(jobId: string) {
    return this._jobs.find(job => job.getId() === jobId);
  }

  public getOnGoingJobs() {
    return this.getJobsInstance(this._jobs.filter(job => !job.isJobFinished()));
  }

  public getFinishedJobs() {
    return this.getJobsInstance(this._jobs.filter(job => job.isJobFinished()));
  }

  public getJobsByDataSetId(dataSetId: string) {
    return this.getJobsInstance(
      this._jobs.filter(job => job.getDataSetId() === dataSetId)
    );
  }

  public removeJobById(jobId: string) {
    const jobIndex = this._jobs.findIndex(job => job.getId() === jobId);

    if (jobIndex !== -1) {
      this._jobs.splice(jobIndex, 1);
    }
  }

  /**
   * Retrieves a list of jobs associated with a specific binary ID.
   *
   * @param {string} binaryId - The ID of the binary to filter jobs by.
   * @returns {Job[]} A list of jobs that are associated with the specified binary ID.
   */
  public getJobsByBinaryId(binaryId: string) {
    return this.getJobsInstance(
      this._jobs.filter(job => job.getAssociatedBinaryId() === binaryId)
    );
  }

  public getJobsByGroupUploadedBatchId(groupUploadedBatchId: string) {
    return this.getJobsInstance(
      this._jobs.filter(
        job => job.getAssociatedGroupUploadedBatchId() === groupUploadedBatchId
      )
    );
  }
}
